import React, { useCallback, useEffect, useRef, useState } from "react";

import {
  CalendarDays,
  ChevronDown,
  Eye,
  FileCheck,
  FileText,
  FileX,
  MoreHorizontal,
  Receipt,
  TrashIcon,
} from "lucide-react";

import { TransportistaAssignmentDialog } from "~/components/TransportistaAssignmentDialog";
import { CreditNoteType } from "~/components/credit-notes/CreditNoteTypeSelector";
import { EnhancedCreditNoteDialog } from "~/components/credit-notes/EnhancedCreditNoteDialog";
import { NullifyCreditNoteDialog } from "~/components/credit-notes/NullifyCreditNoteDialog";
import { PartialCreditItem } from "~/components/credit-notes/PartialCreditNoteSelector";
import { Badge } from "~/components/ui/badge";
import { Button } from "~/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "~/components/ui/dropdown-menu";
import { ListSkeleton } from "~/components/ui/skeletons";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "~/components/ui/tooltip";
import { toast } from "~/hooks/use-toast";
import { useFacturaStockCheck } from "~/hooks/useFacturaStockCheck";
import { supabase } from "~/integrations/supabase/client";
import { bsaleFetch } from "~/services/bsaleProxy";
import { CreditNoteNullificationService } from "~/services/creditNoteNullificationService";
import { useProfileStore } from "~/store/profileStore";

import { EmissionDateDialog } from "./EmissionDateDialog";

// Define a more flexible document interface that matches what's in the database
interface TaxDocument {
  id: string;
  sales_request_id: string;
  type?: string;
  document_type?: string;
  external_id?: number;
  bsale_number?: string;
  url_public_view?: string;
  url_pdf?: string;
  created_at: string;
  cancelled?: boolean;
  total_amount?: number;
  credit_note_type?: string;
  is_exempt?: boolean;
  state?: number;
  uploaded_by?: string | null;
  uploaded_by_name?: string | null;
}

interface DocumentDetail {
  id: number;
  lineNumber: number;
  quantity: number;
  netUnitValue: number;
  netAmount: number;
  variant: {
    description: string;
    code: string;
  };
}

interface TaxDocumentsSectionProps {
  salesRequestId: string;
  orderStatus?: string;
}

export const EnhancedTaxDocumentsSection: React.FC<
  TaxDocumentsSectionProps
> = ({ salesRequestId, orderStatus }) => {
  const [documents, setDocuments] = useState<TaxDocument[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [generatingFactura, setGeneratingFactura] = useState(false);
  const isGeneratingFacturaRef = useRef(false); // Synchronous guard to prevent double-clicks
  const [processingCreditNote, setProcessingCreditNote] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState<TaxDocument | null>(
    null
  );
  const [creditNoteDialogOpen, setCreditNoteDialogOpen] = useState(false);
  const [documentDetails, setDocumentDetails] = useState<DocumentDetail[]>([]);
  const [loadingDetails, setLoadingDetails] = useState(false);

  // Credit note nullification state
  const [showNullifyDialog, setShowNullifyDialog] = useState(false);
  const [
    selectedCreditNoteForNullification,
    setSelectedCreditNoteForNullification,
  ] = useState<TaxDocument | null>(null);

  // Transportista assignment state
  const [showTransportistaDialog, setShowTransportistaDialog] = useState(false);

  // Superadmin-only: custom (back-dated) emission date selection
  const [showEmissionDateDialog, setShowEmissionDateDialog] = useState(false);
  // Holds the chosen emission date (YYYY-MM-DD) while the optional transportista
  // step runs; a ref avoids stale closures in the dialog callback.
  const pendingEmissionDateRef = useRef<string | undefined>(undefined);
  // Carries the "emit as Factura Exenta" choice through the optional
  // transportista step into executeFacturaGeneration.
  const pendingIsExentoRef = useRef<boolean>(false);

  const { role: userRole } = useProfileStore();
  const isAdmin = userRole === "admin" || userRole === "superadmin";
  const isSuperadmin = userRole === "superadmin";
  const isLogistica = userRole === "logistica";

  // Anticipate the server-side finished-goods stock gate so the "Generar
  // factura" button can be disabled (with a reason) before any request is sent.
  // The edge function remains the authoritative block; this is UX only.
  const canFacturar =
    orderStatus === "por_facturar" && (isAdmin || isLogistica);
  const { data: stockCheck } = useFacturaStockCheck(salesRequestId, canFacturar);
  const stockShortfall = stockCheck?.insufficient ?? [];
  const hasStockShortfall = stockShortfall.length > 0;

  // Fetch tax documents from the database
  const fetchDocuments = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const { data, error: fetchError } = await supabase
        .from("sales_request_documents")
        .select("*")
        .eq("sales_request_id", salesRequestId)
        .is("deleted_at", null) // Exclude soft-deleted documents
        .order("created_at", { ascending: false });

      if (fetchError) {
        console.error("Error fetching documents:", fetchError);
        setError("No se pudieron cargar los documentos tributarios");
        toast({
          title: "Error",
          description: "No se pudieron cargar los documentos tributarios",
          variant: "destructive",
        });
      } else {
        // Filter to only show tax documents
        const taxDocs = (data || []).filter((doc) => {
          const docType = doc.document_type || doc.type || "";
          return (
            docType === "factura" ||
            docType === "nota_de_credito" ||
            docType === "nota_de_debito" ||
            docType === "nota_de_debito_anulacion"
          );
        });

        // Hydrate uploader display names so the card can show "Creada por X".
        // sales_request_documents.uploaded_by has no FK to profiles, so we
        // resolve in a second query and stitch in JS instead of a PostgREST embed.
        const uploaderIds = Array.from(
          new Set(
            taxDocs
              .map((doc) => doc.uploaded_by)
              .filter((id): id is string => !!id)
          )
        );
        let uploaderNameById: Record<string, string> = {};
        if (uploaderIds.length > 0) {
          const { data: profilesData, error: profilesError } = await supabase
            .from("profiles")
            .select("id, name, lastname")
            .in("id", uploaderIds);
          if (profilesError) {
            console.error("Error fetching uploader profiles:", profilesError);
          } else {
            uploaderNameById = Object.fromEntries(
              (profilesData || []).map((p) => [
                p.id,
                [p.name, p.lastname].filter(Boolean).join(" ").trim() || p.id,
              ])
            );
          }
        }

        const taxDocsWithUploader = taxDocs.map((doc) => ({
          ...doc,
          uploaded_by_name: doc.uploaded_by
            ? (uploaderNameById[doc.uploaded_by] ?? null)
            : null,
        }));
        setDocuments(taxDocsWithUploader as TaxDocument[]);
      }
    } catch (err) {
      console.error("Error fetching tax documents:", err);
      setError("No se pudieron cargar los documentos tributarios");
      toast({
        title: "Error",
        description: "No se pudieron cargar los documentos tributarios",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  }, [salesRequestId]);

  // Load documents on component mount
  useEffect(() => {
    fetchDocuments();
  }, [fetchDocuments]);

  // Function to fetch document details from BSale
  const fetchDocumentDetails = async (
    document: TaxDocument
  ): Promise<DocumentDetail[]> => {
    if (!document.external_id) {
      throw new Error("No se encontró el ID del documento en BSale");
    }

    setLoadingDetails(true);
    try {
      const { data: sessionData } = await supabase.auth.getSession();
      const anonKey = sessionData?.session?.access_token || "";
      const authHeader = import.meta.env.VITE_SUPABASE_KEY || anonKey;

      const response = await bsaleFetch(
        `/documents/${document.external_id}/details.json`
      );

      if (!response.ok) {
        throw new Error(
          `Error al obtener detalles del documento: ${response.status}`
        );
      }

      const detailsData = await response.json();
      return detailsData.items || [];
    } catch (error) {
      console.error("Error fetching document details:", error);
      throw error;
    } finally {
      setLoadingDetails(false);
    }
  };

  // Function to open the document URL in a new tab
  const viewDocument = (url: string) => {
    window.open(url, "_blank", "noopener,noreferrer");
  };

  // Function to open the enhanced credit note dialog
  const openCreditNoteDialog = async (doc: TaxDocument) => {
    setSelectedDocument(doc);

    try {
      const details = await fetchDocumentDetails(doc);
      setDocumentDetails(details);
      setCreditNoteDialogOpen(true);
    } catch (error) {
      toast({
        title: "Error",
        description: "No se pudieron cargar los detalles del documento",
        variant: "destructive",
      });
    }
  };

  // Function to handle credit note creation
  const handleCreditNoteSubmit = async (params: {
    motive: string;
    credit_note_type: CreditNoteType;
    partial_items?: PartialCreditItem[];
    administrative_amount?: number;
    administrative_reason?: string;
    target_detail_index?: number;
  }) => {
    if (!selectedDocument) return;

    setProcessingCreditNote(true);
    try {
      const { data: sessionData } = await supabase.auth.getSession();
      const anonKey = sessionData?.session?.access_token || "";
      const authHeader = import.meta.env.VITE_SUPABASE_KEY || anonKey;

      const requestPayload = {
        sales_request_document_id: selectedDocument.id,
        ...params,
      };

      console.log("🔍 Sending credit note request:", requestPayload);

      // Call the enhanced anular-factura API
      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL || "https://mdrvhekhimhcwydrpueo.supabase.co"}/functions/v1/anular-factura`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${authHeader}`,
          },
          body: JSON.stringify(requestPayload),
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        console.warn("Credit note creation failed:", errorData);
        toast({
          title: "Error",
          description:
            errorData.error || "No se pudo generar la nota de crédito",
          variant: "destructive",
        });
      } else {
        const responseData = await response.json();
        console.log("Credit note created successfully:", responseData);

        // The server auto-restores finished-goods stock for returned boxes (full annulment or
        // partial devolución). Surface it so the seller knows bodega doesn't need to reload it.
        const restoredLines = Array.isArray(
          responseData?.stockRestore?.result?.restored
        )
          ? responseData.stockRestore.result.restored
          : [];
        const restoredBoxes = restoredLines.reduce(
          (sum: number, line: { quantity?: number }) =>
            sum + (Number(line?.quantity) || 0),
          0
        );
        const restoreFailed =
          responseData?.stockRestore && responseData.stockRestore.ok === false;

        const baseMessage =
          responseData.message || "Nota de crédito generada exitosamente";
        const description =
          restoredBoxes > 0
            ? `${baseMessage} Se repusieron ${restoredBoxes} caja(s) al stock.`
            : restoreFailed
              ? `${baseMessage} No se pudo reponer el stock automáticamente; avisar a bodega.`
              : baseMessage;

        toast({
          title: "Éxito",
          description,
          variant: restoreFailed ? "destructive" : "default",
        });

        // Close the dialog and refresh the documents list
        setCreditNoteDialogOpen(false);
        fetchDocuments();
      }
    } catch (error) {
      console.error("Error creating credit note:", error);
      toast({
        title: "Error",
        description: "No se pudo generar la nota de crédito",
        variant: "destructive",
      });
    } finally {
      setProcessingCreditNote(false);
    }
  };

  // Function to manually generate a factura
  const generateFactura = async () => {
    // Synchronous guard - prevents race condition from double-clicks
    if (isGeneratingFacturaRef.current) return;

    // Normal button = today's date (no back-dating), afecta (with IVA).
    pendingEmissionDateRef.current = undefined;
    pendingIsExentoRef.current = false;

    // If the order status is "por_facturar", show transportista assignment dialog first
    if (orderStatus === "por_facturar") {
      setShowTransportistaDialog(true);
      return;
    }

    // Otherwise, proceed with normal factura generation
    await executeFacturaGeneration();
  };

  // Exenta path: emit a Factura Exenta (SII código 34, no IVA). Ad-hoc per
  // invoice; goes through the same optional transportista step as the normal flow.
  const generateFacturaExenta = async () => {
    if (isGeneratingFacturaRef.current) return;

    pendingEmissionDateRef.current = undefined;
    pendingIsExentoRef.current = true;

    if (orderStatus === "por_facturar") {
      setShowTransportistaDialog(true);
      return;
    }

    await executeFacturaGeneration(undefined, true);
  };

  // Superadmin custom-date path: same flow as generateFactura but carrying the
  // chosen emission date through the optional transportista step.
  const handleGenerateWithCustomDate = async (emissionDate: string) => {
    if (isGeneratingFacturaRef.current) return;

    pendingEmissionDateRef.current = emissionDate;
    pendingIsExentoRef.current = false;
    setShowEmissionDateDialog(false);

    if (orderStatus === "por_facturar") {
      setShowTransportistaDialog(true);
      return;
    }

    await executeFacturaGeneration(emissionDate);
  };

  // Function to update sales request status
  const updateSalesRequestStatus = async (newStatus: string) => {
    try {
      const { error } = await supabase
        .from("sales_request")
        .update({
          status: newStatus,
          updated_at: new Date().toISOString(),
        })
        .eq("id", salesRequestId);

      if (error) {
        console.error("Error updating sales request status:", error);
        throw error;
      }

      // If status is set to in_transit (from factura creation), update history to show "facturado"
      // since no transportista is assigned yet
      if (newStatus === "in_transit") {
        const { data: latestHistory } = await supabase
          .from("sales_request_status_history")
          .select("id")
          .eq("sales_request_id", salesRequestId)
          .eq("new_status", "in_transit")
          .order("changed_at", { ascending: false })
          .limit(1)
          .single();

        if (latestHistory) {
          await supabase
            .from("sales_request_status_history")
            .update({ new_status: "facturado" })
            .eq("id", latestHistory.id);
        }
      }
    } catch (error) {
      console.error("Failed to update sales request status:", error);
      throw error;
    }
  };

  // Separate function for actual factura generation.
  // emissionDate (YYYY-MM-DD, superadmin only) back-dates the factura when set.
  // isExento emits a Factura Exenta (SII código 34, no IVA).
  const executeFacturaGeneration = async (
    emissionDate?: string,
    isExento?: boolean
  ) => {
    // Synchronous guard - prevents race condition from double-clicks
    if (isGeneratingFacturaRef.current) return;
    isGeneratingFacturaRef.current = true;

    setGeneratingFactura(true);
    try {
      const { data: sessionData } = await supabase.auth.getSession();
      const accessToken = sessionData?.session?.access_token || "";
      // A custom (back-dated) emission requires the edge function to identify the
      // acting user to authorize superadmin — so send the user's access token.
      // The anon/publishable key resolves to no user, so it can't be used here.
      // The normal path keeps its existing header behavior unchanged.
      const authHeader = emissionDate
        ? accessToken
        : import.meta.env.VITE_SUPABASE_KEY || accessToken;

      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL || "https://mdrvhekhimhcwydrpueo.supabase.co"}/functions/v1/create-factura`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${authHeader}`,
          },
          body: JSON.stringify({
            salesRequestId,
            skipBsale: true,
            ...(emissionDate ? { emissionDate } : {}),
            ...(isExento ? { isExento: true } : {}),
          }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        console.warn("Factura creation failed:", errorData);

        if (errorData.code === "IN_FLIGHT") {
          toast({
            title: "Facturación en curso",
            description:
              "Ya hay una facturación en curso para esta nota de venta. Espere un momento antes de reintentar.",
            variant: "default",
          });
        } else if (
          errorData.code === "BSALE_UNCERTAIN" ||
          response.status === 504
        ) {
          // BSale timed out: the DTE may exist anyway. A retry is safe — the
          // edge function recovers the emitted document instead of duplicating.
          toast({
            title: "BSale no respondió a tiempo",
            description:
              "La factura puede haberse emitido de todas formas. Espere un momento y reintente: el sistema recuperará el documento emitido en vez de duplicarlo.",
            variant: "destructive",
          });
        } else if (errorData.code === "RECOVERY_AMBIGUOUS") {
          toast({
            title: "Requiere regularización manual",
            description:
              errorData.error ||
              "Existen múltiples DTE sin registrar en BSale para esta nota de venta.",
            variant: "destructive",
          });
        } else if (
          errorData.code === "INSUFFICIENT_STOCK" ||
          errorData.code === "STOCK_CHECK_FAILED"
        ) {
          // Finished-goods stock gate rejected the emission (also HTTP 409, so
          // this must be handled before the generic "Factura ya existe" branch).
          toast({
            title: "Stock insuficiente",
            description:
              errorData.error ||
              "No hay stock de producto terminado suficiente para emitir la factura.",
            variant: "destructive",
          });
        } else if (response.status === 409) {
          toast({
            title: "Factura ya existe",
            description: "Ya existe una factura para esta nota de venta",
            variant: "default",
          });
        } else {
          // Show BSale error details if available
          const bsaleError =
            errorData.details?.error || errorData.details?.message || "";
          toast({
            title: "Error",
            description: bsaleError
              ? `No se pudo generar la factura: ${bsaleError}`
              : "No se pudo generar la factura",
            variant: "destructive",
          });
        }
      } else {
        const result = await response.json().catch(() => ({}) as any);
        console.log("Factura created successfully", result);

        // Mirror the status the edge function set. An all-service factura skips
        // "Facturado" and goes straight to "delivered"; otherwise it's "in_transit".
        const newStatus = result.allServices ? "delivered" : "in_transit";
        await updateSalesRequestStatus(newStatus);

        toast({
          title: result.recovered ? "Factura recuperada" : "Factura generada",
          description: result.recovered
            ? "Se recuperó la factura emitida en un intento anterior interrumpido; no se emitió un duplicado."
            : result.allServices
              ? "La factura de servicio ha sido generada y la nota de venta quedó como entregada."
              : "La factura ha sido generada exitosamente y la nota de venta está en tránsito",
          variant: "default",
        });
        fetchDocuments();
      }
    } catch (error) {
      console.error("Error generating factura:", error);
      toast({
        title: "Error",
        description: "No se pudo generar la factura",
        variant: "destructive",
      });
    } finally {
      setGeneratingFactura(false);
      isGeneratingFacturaRef.current = false;
      pendingEmissionDateRef.current = undefined;
      pendingIsExentoRef.current = false;
    }
  };

  // Function to handle credit note nullification
  const handleNullifyCreditNote = async (doc: TaxDocument) => {
    setSelectedCreditNoteForNullification(doc);
    setShowNullifyDialog(true);
  };

  // Handle successful nullification
  const handleNullificationSuccess = (result: any) => {
    console.log("Credit note nullified successfully:", result);
    toast({
      title: "Éxito",
      description: "La nota de crédito ha sido anulada correctamente",
    });

    // Refresh the documents to show updated status
    fetchDocuments();

    // Close dialog
    setShowNullifyDialog(false);
    setSelectedCreditNoteForNullification(null);
  };

  // Check if a credit note can be nullified
  const canNullifyDocument = (doc: TaxDocument): boolean => {
    const docType = doc.document_type || doc.type || "";

    // Only credit notes can be nullified
    if (docType !== "nota_de_credito") {
      return false;
    }

    // Can't nullify if already cancelled
    if (doc.cancelled) {
      return false;
    }

    // Can't nullify if state is 0 (already cancelled)
    if (doc.state === 0) {
      return false;
    }

    // Use the service to check if it can be nullified
    const { canNullify } = CreditNoteNullificationService.canNullify(doc);
    return canNullify;
  };

  // Document type labels
  const getDocumentTypeLabel = (doc: TaxDocument) => {
    const type = doc.document_type || doc.type || "";

    switch (type) {
      case "factura":
        return "Factura Electrónica";
      case "nota_de_credito": {
        const creditType = doc.credit_note_type || "full";
        return `Nota de Crédito${
          creditType === "partial"
            ? " Parcial"
            : creditType === "administrative"
              ? " Administrativa"
              : ""
        }`;
      }
      case "nota_de_debito":
        return "Nota de Débito";
      case "nota_de_debito_anulacion":
        return "Nota de Débito (Anulación)";
      default:
        return type.charAt(0).toUpperCase() + type.slice(1);
    }
  };

  // Check if the order already has a factura that isn't cancelled
  const hasActiveFactura = documents.some(
    (doc) =>
      (doc.type === "factura" || doc.document_type === "factura") &&
      !doc.cancelled
  );

  // Only allow factura generation when the order is in "por_facturar".
  // This prevents facturas from being emitted while the order is still in
  // earlier columns (e.g. "aprobado"/accepted), by logistica or admin users.
  const showGenerateButton =
    orderStatus === "por_facturar" &&
    (isLogistica || isAdmin) &&
    !hasActiveFactura &&
    !loading;

  const formatCurrency = (amount: number): string => {
    return new Intl.NumberFormat("es-CL", {
      style: "currency",
      currency: "CLP",
      minimumFractionDigits: 0,
    }).format(amount);
  };

  const facturaButtonInner = generatingFactura ? (
    <>
      <div className="mr-2 h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
      Generando...
    </>
  ) : (
    <>
      <FileCheck className="mr-2 h-4 w-4" />
      Generar factura
    </>
  );

  const facturaDisabled = generatingFactura || hasStockShortfall;

  // When stock is short the button is disabled; a disabled button emits no hover
  // events, so the tooltip lives on a wrapping span (standard shadcn pattern).
  const withStockTooltip = (node: React.ReactNode) =>
    hasStockShortfall ? (
      <Tooltip>
        <TooltipTrigger asChild>
          <span tabIndex={0} className="inline-flex">
            {node}
          </span>
        </TooltipTrigger>
        <TooltipContent className="max-w-xs">
          <p className="font-medium">Sin stock de producto terminado</p>
          <ul className="mt-1 list-disc space-y-0.5 pl-4 text-xs">
            {stockShortfall.map((i) => (
              <li key={i.productId}>
                {i.name}: disponible {i.available}, requiere {i.required}
              </li>
            ))}
          </ul>
        </TooltipContent>
      </Tooltip>
    ) : (
      node
    );

  return (
    <>
      <div className="overflow-hidden rounded-xl border border-gray-300 bg-white shadow-sm">
        <div className="flex items-center justify-between border-b border-gray-200 bg-gray-100 px-5 py-3">
          <h3 className="flex items-center text-lg font-semibold text-gray-800">
            <FileText className="mr-2 h-5 w-5 text-green-600" />
            Documentos Tributarios
          </h3>

          {showGenerateButton &&
            // Split button: the primary action emits a regular factura electrónica
            // (afecta, con IVA); the caret exposes "Factura exenta" (any emitter)
            // and, for superadmin, back-dated emission.
            withStockTooltip(
              <div className="flex items-center">
                <Button
                  variant="default"
                  size="sm"
                  className="rounded-r-none bg-green-600 text-white hover:bg-green-700"
                  onClick={generateFactura}
                  disabled={facturaDisabled}
                >
                  {facturaButtonInner}
                </Button>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      variant="default"
                      size="sm"
                      className="rounded-l-none border-l border-green-700 bg-green-600 px-2 text-white hover:bg-green-700"
                      disabled={facturaDisabled}
                      aria-label="Más opciones de facturación"
                    >
                      <ChevronDown className="h-4 w-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-60">
                    <DropdownMenuItem
                      className="whitespace-nowrap"
                      onClick={generateFacturaExenta}
                    >
                      <Receipt className="mr-2 h-4 w-4 shrink-0" />
                      Factura exenta (sin IVA)
                    </DropdownMenuItem>
                    {isSuperadmin && (
                      <DropdownMenuItem
                        className="whitespace-nowrap"
                        onClick={() => setShowEmissionDateDialog(true)}
                      >
                        <CalendarDays className="mr-2 h-4 w-4 shrink-0" />
                        Generar con fecha de emisión
                      </DropdownMenuItem>
                    )}
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            )}
        </div>

        <div className="p-4">
          {loading ? (
            <ListSkeleton rows={2} />
          ) : error ? (
            <div className="py-8 text-center text-red-500">
              <p>{error}</p>
            </div>
          ) : documents.length > 0 ? (
            <div className="space-y-4">
              {documents.map((doc) => {
                const docType = doc.type || doc.document_type || "";
                const isFactura = docType === "factura";
                const isNotaCredito = docType === "nota_de_credito";
                const isNotaDebito = docType === "nota_de_debito";
                const isCancelled = !!doc.cancelled;

                return (
                  <div
                    key={doc.id}
                    className="flex items-center gap-3 rounded border bg-gray-50 p-4 hover:bg-gray-100"
                  >
                    <FileText
                      className={`h-10 w-10 flex-shrink-0 ${
                        isNotaCredito
                          ? "text-blue-500"
                          : isNotaDebito
                            ? "text-orange-500"
                            : "text-green-500"
                      }`}
                    />
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 text-sm font-medium">
                        {getDocumentTypeLabel(doc)}
                        {isFactura && doc.is_exempt && (
                          <Badge variant="secondary" className="text-xs">
                            Exenta
                          </Badge>
                        )}
                        {isCancelled && (
                          <Badge variant="destructive" className="text-xs">
                            Anulado
                          </Badge>
                        )}
                        {doc.credit_note_type &&
                          doc.credit_note_type !== "full" && (
                            <Badge variant="secondary" className="text-xs">
                              {doc.credit_note_type === "partial"
                                ? "Parcial"
                                : "Administrativa"}
                            </Badge>
                          )}
                      </div>
                      <div className="text-xs text-gray-500">
                        Número: {doc.bsale_number || doc.external_id || "N/A"}
                      </div>
                      <div className="text-xs text-gray-500">
                        Fecha: {new Date(doc.created_at).toLocaleDateString()}
                      </div>
                      {doc.total_amount && (
                        <div className="text-xs text-gray-500">
                          Monto: {formatCurrency(doc.total_amount)}
                        </div>
                      )}
                      {isFactura && doc.uploaded_by_name && (
                        <div className="text-xs text-gray-500">
                          Emitida por: {doc.uploaded_by_name}
                        </div>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      {doc.url_public_view && (
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Button
                              variant="outline"
                              size="icon"
                              className="h-9 w-9"
                              onClick={() =>
                                viewDocument(doc.url_public_view || "")
                              }
                              aria-label="Ver documento"
                            >
                              <Eye className="h-4 w-4" />
                            </Button>
                          </TooltipTrigger>
                          <TooltipContent>Ver documento</TooltipContent>
                        </Tooltip>
                      )}

                      {((isFactura && !isCancelled && isAdmin) ||
                        (isNotaCredito &&
                          canNullifyDocument(doc) &&
                          isAdmin)) && (
                        <DropdownMenu>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <DropdownMenuTrigger asChild>
                                <Button
                                  variant="outline"
                                  size="icon"
                                  className="h-9 w-9"
                                  disabled={loadingDetails}
                                  aria-label="Más acciones"
                                >
                                  <MoreHorizontal className="h-4 w-4" />
                                </Button>
                              </DropdownMenuTrigger>
                            </TooltipTrigger>
                            <TooltipContent>Más acciones</TooltipContent>
                          </Tooltip>
                          <DropdownMenuContent align="end">
                            {isFactura && !isCancelled && isAdmin && (
                              <DropdownMenuItem
                                onClick={() => openCreditNoteDialog(doc)}
                                disabled={loadingDetails}
                                className="text-red-600 focus:text-red-600"
                              >
                                <TrashIcon className="mr-2 h-4 w-4" />
                                {loadingDetails ? "Cargando..." : "Generar NC"}
                              </DropdownMenuItem>
                            )}
                            {isNotaCredito &&
                              canNullifyDocument(doc) &&
                              isAdmin && (
                                <DropdownMenuItem
                                  onClick={() => handleNullifyCreditNote(doc)}
                                  className="text-red-600 focus:text-red-600"
                                >
                                  <FileX className="mr-2 h-4 w-4" />
                                  Anular NC
                                </DropdownMenuItem>
                              )}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="py-8 text-center text-gray-500">
              <FileText className="mx-auto mb-2 h-12 w-12 text-gray-300" />
              <p>No hay documentos tributarios disponibles</p>
            </div>
          )}
        </div>
      </div>

      {/* Enhanced Credit Note Dialog */}
      <EnhancedCreditNoteDialog
        isOpen={creditNoteDialogOpen}
        onClose={() => setCreditNoteDialogOpen(false)}
        onSubmit={handleCreditNoteSubmit}
        documentDetails={documentDetails}
        isSubmitting={processingCreditNote}
        documentNumber={
          (typeof selectedDocument?.bsale_number === "number"
            ? selectedDocument.bsale_number
            : selectedDocument?.external_id) || undefined
        }
        documentAmount={selectedDocument?.total_amount}
      />

      {/* Credit Note Nullification Dialog */}
      {selectedCreditNoteForNullification && (
        <NullifyCreditNoteDialog
          isOpen={showNullifyDialog}
          onClose={() => {
            setShowNullifyDialog(false);
            setSelectedCreditNoteForNullification(null);
          }}
          creditNote={selectedCreditNoteForNullification}
          onSuccess={handleNullificationSuccess}
        />
      )}

      {/* Transportista Assignment Dialog */}
      <TransportistaAssignmentDialog
        open={showTransportistaDialog}
        onOpenChange={setShowTransportistaDialog}
        salesRequestId={salesRequestId}
        onAssignmentComplete={() => {
          // Refresh the component if needed
          console.log("Transportista assigned successfully");
        }}
        onGenerateFactura={() =>
          executeFacturaGeneration(
            pendingEmissionDateRef.current,
            pendingIsExentoRef.current
          )
        }
      />

      {/* Emission Date Dialog (superadmin only) */}
      {isSuperadmin && (
        <EmissionDateDialog
          open={showEmissionDateDialog}
          onOpenChange={setShowEmissionDateDialog}
          onConfirm={handleGenerateWithCustomDate}
        />
      )}
    </>
  );
};

export default EnhancedTaxDocumentsSection;
